import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../../styles/ContractReport.css';
import CustomContainer from '../CustomElements/CustomContainer';
import FormContainer from '../CustomElements/FormContainer';
import CustomButton from '../CustomElements/CustomButton';
import Selector from '../CustomElements/Selector';
import { useButtonValidation } from '../../customHook/useButtonValidation';
import { handleUniversalChange } from '../../utils/formUtils';
import { getContractReportFromServer } from '../../redux/actions/documentation.action';
import DateRangePicker from '../CustomElements/DateRangePicker';
import { load, remove } from '../../redux/actions/loader.action';
import { convertDateToMs } from '../../utils/convertToDate';
import CustomTitle from '../CustomElements/CustomTitle';
import Modal from '../Modal/Modal';
import Preloader from '../CustomElements/Preloader';
import { initDepartmentOptionsFromServer, initOptionsDirectoratesFromServer } from '../../redux/actions/options.action';
import useOptionsLoader from '../../customHook/useOptionsLoader';
import ConfirmationPage from '../CustomElements/ConfirmationPage';

function ContractReport({ t }) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();

  const [divisionName, setDivisionName] = useState();

  // получаем опции из стора
  const options = useSelector((state) => state.options);

  // Получаем данные о процессе
  const loader = useSelector((state) => state.loader);

  const [isOpen, setIsOpen] = useState(false);

  const closeModal = () => {
    setIsOpen(false);
    setDivisionName();
    dispatch(remove());
  };

  // Состояние для формы
  const initialFormData = {
    roadId: '',
    departmentId: '',
    directorateId: '',
    startDate: '',
    endDate: '',
    reportType: '',
  };

  const reportTypes = [
    { value: 'onOff', label: t?.reportTypesOnOff || 'Включение/выключение' },
    { value: 'onOff10', label: t?.reportTypesOnOff10 || 'Включение/выключение - 10 мин' },
    { value: 'conn', label: t?.reportTypesConn || 'Подключение/отключение' },
    { value: 'restrict', label: t?.reportTypesRestrict || 'Ограждение' },
    { value: 'service', label: t?.reportTypesService || 'Отчет об оказанных услугах' },
    { value: 'serviceXlsx', label: t?.reportTypesServiceXlsx || 'Отчет об оказанных услугах (xlsx)' },
    { value: 'general', label: t?.reportTypesGeneral || 'Общий отчет' },
  ];

  const [formData, setFormData] = useState(initialFormData);
  const [optionalFields] = useState(['departmentId']);

  // Функция для проверки валидности формы
  const isButtonDisabled = useButtonValidation(formData, optionalFields);

  // Обработчик отправки формы
  const handlerSubmit = (event) => {
    event.preventDefault();
    // Переводим в состояние загрузки
    dispatch(load());

    // Создаем новый объект payload, копируя все свойства из formData
    const payload = { ...formData };

    // Если formData содержит startDate, преобразуем его и обновляем в payload
    if (formData?.startDate) {
      payload.startDate = convertDateToMs(formData.startDate);
    }

    // Если formData содержит endDate, преобразуем его и обновляем в payload
    if (formData?.endDate) {
      payload.endDate = convertDateToMs(formData.endDate);
    }
    // Отправляем данные на сервер через Redux
    dispatch(getContractReportFromServer(payload, divisionName));
  };

  //отслеживаем выполнение запроса
  useEffect(() => {
    // Если нет ошибок, открываем модальное окно отчета
    if (!loader?.isLoading && loader?.isSuccess) {
      setIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loader?.isLoading, loader?.isSuccess]);

  useEffect(() => {
    // при закрытии компонента чистим стейт загрузки
    return () => {
      dispatch(remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //хук для запроса опций для дирекций и контроля селекта
  const messageForSelectDirectorates = t?.roadPlaceholder || 'Выберите дорогу';
  const directoratesControl = useOptionsLoader(
    formData,
    setFormData,
    ['directorateId', 'departmentId'],
    'roadId',
    initOptionsDirectoratesFromServer,
    messageForSelectDirectorates
  );

  //хук для запроса опций для подразделений и контроля селекта
  const messageForSelectDepartments = t?.directoratePlaceholder || 'Выберите дирекцию';
  const departmentsControl = useOptionsLoader(
    formData,
    setFormData,
    ['departmentId'],
    'directorateId',
    initDepartmentOptionsFromServer,
    messageForSelectDepartments
  );

  return (
    <div className="reports-item">
      <CustomContainer>
        <CustomTitle titleText={t?.contractReportTitle || 'ОТЧЕТ ОБ ОКАЗАННЫХ УСЛУГАХ ПО ДОГОВОРУ'} />
        <FormContainer>
          <Selector
            column="left"
            options={options?.roads}
            onChange={(selectedOption) => {
              handleUniversalChange(formData, setFormData, null, 'roadId', selectedOption, 'select');
            }}
            label={t?.roadLabel || 'Дорога *'}
            placeholder={t?.roadPlaceholder || 'Выберите дорогу'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={options?.roads?.find((option) => option.value === formData.roadId)}
            isMulti={false}
            name="roadId"
            isDisabled={loader?.isLoading}
          />
          <Selector
            column="right"
            options={options?.directorates}
            onChange={(selectedOption) => {
              handleUniversalChange(formData, setFormData, null, 'directorateId', selectedOption, 'select');
              setDivisionName(selectedOption?.label);
            }}
            label={t?.directorateLabel || 'Дирекция *'}
            placeholder={t?.directoratePlaceholder || 'Выберите дирекцию'}
            value={
              formData.directorateId
                ? options?.directorates?.find((option) => option.value === formData.directorateId)
                : null
            }
            isMulti={false}
            name="directorateId"
            isDisabled={directoratesControl?.isSelectorDisabled}
            message={directoratesControl?.message}
            customClick={directoratesControl?.onSelectorChange}
            width="100%"
            height="var(--sadr-font-size-60)"
          />
          <Selector
            column="left"
            options={options?.departments}
            onChange={(selectedOption) => {
              handleUniversalChange(formData, setFormData, null, 'departmentId', selectedOption, 'select');
              const newDivisionName =
                selectedOption?.label ||
                options?.directorates?.find((d) => d.value === Number(formData?.directorateId))?.label;
              setDivisionName(newDivisionName);
            }}
            label={t?.departmentLabel || 'Подразделение'}
            placeholder={t?.departmentPlaceholder || 'Выберите подразделение'}
            width="100%"
            height="var(--sadr-font-size-60)"
            value={
              formData.departmentId && options?.departments
                ? options?.departments?.find((option) => option.value === formData.departmentId)
                : null
            }
            isMulti={false}
            name="departmentId"
            isDisabled={departmentsControl?.isSelectorDisabled || loader?.isLoading}
            message={departmentsControl?.message}
            customClick={departmentsControl?.onSelectorChange}
          />

          <DateRangePicker
            column="right"
            width="100%"
            height="var(--sadr-font-size-60)"
            formData={formData}
            setFormData={setFormData}
            startDateField="startDate" // имя поля для даты начала, с дефолтным значением
            endDateField="endDate" // имя поля для даты окончания, с дефолтным значением
            name="date"
            label={t?.periodLabel || 'Период *'}
            placeholder={t?.periodPlaceholder || 'Выберите период'}
            topPosition="50%"
            maxDate={new Date()}
            disabled={loader?.isLoading}
          />

          <Selector
            column="left"
            options={reportTypes}
            onChange={(selectedOption) => {
              handleUniversalChange(formData, setFormData, null, 'reportType', selectedOption, 'select');
            }}
            label={t?.reportTypeLabel || 'Тип отчета *'}
            placeholder={t?.reportTypePlaceholder || 'Выберите тип отчета'}
            value={formData.reportType ? reportTypes?.find((option) => option.value === formData.reportType) : null}
            isMulti={false}
            name="reportType"
            width="100%"
            height="var(--sadr-font-size-60)"
          />

          <Preloader isLoading={loader?.isLoading} />
          <CustomButton
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
            text={t?.generateReportButton || 'Сформировать отчет'}
            disabled={isButtonDisabled || loader?.isLoading}
            isButton
            onClick={handlerSubmit}
          />
        </FormContainer>
      </CustomContainer>
      <Preloader isLoading={loader?.isLoading} />
      {isOpen && (
        <Modal isOpen={isOpen} onClose={closeModal} width="700px" height="450px">
          <CustomContainer>
            <ConfirmationPage
              addNew={t?.backToReportForm || 'К форме отчета'}
              confirmText={t?.confirmationReportTitle || 'Отчет об  объеме оказанных услуг по договору сформирован!'}
              onConfirm={closeModal} // Функция для закрытия модального окна или отмены действия
            />
          </CustomContainer>
        </Modal>
      )}
    </div>
  );
}

export default ContractReport;
